/**
 * Composant de galerie photos/vidéos réutilisable pour événements.
 * - Mode "editable=true" : admin peut uploader/supprimer
 * - Mode "editable=false" : affichage seul (portail client, site vitrine)
 *
 * Vidéos = iframes Vimeo pré-sanitisées côté backend, on peut donc les
 * injecter en HTML brut sans risque XSS.
 */
import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Video, Play, Trash2, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { competitionMedia } from '../services/api';

export function EventGallery({ competitionId, photos = [], videos = [], editable = false, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [videoTitle, setVideoTitle] = useState('');
  const [videoEmbed, setVideoEmbed] = useState('');
  const [submittingVideo, setSubmittingVideo] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null); // -1 (aucun) sinon index de photo
  const fileInputRef = useRef(null);

  const MAX_PHOTOS = 30;
  const remainingSlots = MAX_PHOTOS - photos.length;

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > remainingSlots) {
      toast.error(`Vous ne pouvez ajouter que ${remainingSlots} photo(s) de plus (max ${MAX_PHOTOS}).`);
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    let uploaded = 0;
    for (const file of files) {
      try {
        if (file.size > 8 * 1024 * 1024) {
          toast.error(`${file.name} dépasse 8 MB — ignoré.`);
          continue;
        }
        const res = await competitionMedia.uploadPhoto(competitionId, file);
        uploaded += 1;
        setUploadProgress(Math.round((uploaded / files.length) * 100));
        if (res?.data?.photo && onChange) {
          onChange({ photos: [...photos, res.data.photo], videos });
        }
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Upload échoué';
        toast.error(`${file.name} : ${detail}`);
      }
    }
    setUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (uploaded > 0) toast.success(`${uploaded} photo(s) ajoutée(s).`);
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await competitionMedia.deletePhoto(competitionId, photoId);
      if (onChange) onChange({ photos: photos.filter(p => p.id !== photoId), videos });
      toast.success('Photo supprimée');
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleAddVideo = async () => {
    if (!videoEmbed.trim()) {
      toast.error('Collez l\'iframe Vimeo ou l\'URL');
      return;
    }
    setSubmittingVideo(true);
    try {
      const res = await competitionMedia.addVideo(competitionId, {
        title: videoTitle.trim() || null,
        embed_html: videoEmbed.trim(),
      });
      if (res?.data?.video && onChange) {
        onChange({ photos, videos: [...videos, res.data.video] });
      }
      setVideoTitle('');
      setVideoEmbed('');
      setShowVideoForm(false);
      toast.success('Vidéo ajoutée');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Erreur';
      toast.error(detail);
    } finally {
      setSubmittingVideo(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Supprimer cette vidéo ?')) return;
    try {
      await competitionMedia.deleteVideo(competitionId, videoId);
      if (onChange) onChange({ photos, videos: videos.filter(v => v.id !== videoId) });
      toast.success('Vidéo supprimée');
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const hasMedia = photos.length > 0 || videos.length > 0;

  if (!editable && !hasMedia) return null;

  return (
    <div className="space-y-4" data-testid="event-gallery">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800 flex items-center gap-2">
          <ImageIcon size={16} className="text-emerald-600" />
          Galerie photos & vidéos
          {hasMedia && (
            <span className="text-[11px] text-neutral-500 font-normal">
              ({photos.length} photo{photos.length > 1 ? 's' : ''}
              {videos.length > 0 ? `, ${videos.length} vidéo${videos.length > 1 ? 's' : ''}` : ''})
            </span>
          )}
        </h3>
      </div>

      {editable && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || remainingSlots <= 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="gallery-upload-btn"
          >
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {uploading ? `Upload... ${uploadProgress}%` : `Ajouter des photos (${remainingSlots} restantes)`}
          </button>
          <button
            type="button"
            onClick={() => setShowVideoForm(!showVideoForm)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors"
            data-testid="gallery-add-video-btn"
          >
            <Video size={13} />
            Ajouter une vidéo Vimeo
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
            onChange={handleFilesSelected}
            className="hidden"
            data-testid="gallery-file-input"
          />
          <p className="w-full text-[11px] text-neutral-500">
            Formats : JPG, PNG, WebP, HEIC — 8 MB max par photo — 30 photos max par événement.
          </p>
        </div>
      )}

      {/* Formulaire ajout vidéo */}
      {editable && showVideoForm && (
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-2">
          <div>
            <label className="text-[11px] font-medium text-purple-800 block mb-1">Titre (optionnel)</label>
            <input
              type="text"
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
              placeholder="Ex: Remise des prix 2026"
              className="w-full px-2.5 py-1.5 text-xs border border-purple-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="gallery-video-title"
            />
          </div>
          <div>
            <label className="text-[11px] font-medium text-purple-800 block mb-1">
              Iframe Vimeo complet <span className="text-purple-500">(ou URL <code>https://vimeo.com/123456789</code>)</span>
            </label>
            <textarea
              value={videoEmbed}
              onChange={(e) => setVideoEmbed(e.target.value)}
              placeholder='<iframe src="https://player.vimeo.com/video/..." ...></iframe>'
              rows={3}
              className="w-full px-2.5 py-1.5 text-xs font-mono border border-purple-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="gallery-video-embed"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={handleAddVideo}
              disabled={submittingVideo || !videoEmbed.trim()}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
              data-testid="gallery-video-save"
            >
              {submittingVideo ? 'Ajout...' : 'Ajouter'}
            </button>
            <button
              type="button"
              onClick={() => { setShowVideoForm(false); setVideoEmbed(''); setVideoTitle(''); }}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-white text-purple-700 border border-purple-300 hover:bg-purple-50"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Grille photos */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {photos.map((photo, idx) => (
            <div
              key={photo.id}
              className="relative aspect-square rounded-md overflow-hidden bg-neutral-100 border border-neutral-200 group cursor-pointer"
              onClick={() => setLightboxIndex(idx)}
              data-testid={`gallery-photo-${photo.id}`}
            >
              <img
                src={photo.url}
                alt={photo.filename || `Photo ${idx + 1}`}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              {editable && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                  className="absolute top-1 right-1 p-1 rounded-full bg-red-500/90 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-opacity"
                  title="Supprimer"
                  data-testid={`gallery-photo-delete-${photo.id}`}
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Vidéos */}
      {videos.length > 0 && (
        <div className="space-y-3">
          {videos.map(video => (
            <div key={video.id} className="relative bg-black rounded-lg overflow-hidden" data-testid={`gallery-video-${video.id}`}>
              {video.title && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-2 z-10 flex items-start justify-between gap-2">
                  <p className="text-white text-xs font-medium flex items-center gap-1.5">
                    <Play size={12} fill="currentColor" />
                    {video.title}
                  </p>
                  {editable && (
                    <button
                      type="button"
                      onClick={() => handleDeleteVideo(video.id)}
                      className="p-1 rounded-full bg-red-500/90 text-white hover:bg-red-600 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              )}
              {!video.title && editable && (
                <button
                  type="button"
                  onClick={() => handleDeleteVideo(video.id)}
                  className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-red-500/90 text-white hover:bg-red-600 transition-colors"
                  title="Supprimer"
                >
                  <Trash2 size={13} />
                </button>
              )}
              {/* Iframe pré-sanitisée côté backend */}
              <div
                className="aspect-video w-full [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:border-0"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: video.embed_html }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Lightbox photos */}
      {lightboxIndex !== null && photos[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <X size={20} />
          </button>
          {lightboxIndex > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }}
              className="absolute left-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
          )}
          {lightboxIndex < photos.length - 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }}
              className="absolute right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          )}
          <img
            src={photos[lightboxIndex].url}
            alt={photos[lightboxIndex].filename || `Photo ${lightboxIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-xs">
            {lightboxIndex + 1} / {photos.length}
          </div>
        </div>
      )}
    </div>
  );
}

export default EventGallery;
